import React from 'react';
import styled, { css } from 'styled-components';
import { Flex, Flex3XS, Flex4XS, FlexCol, makeCentered } from '@/components/Flex';
import Typography from '@/components/core/Typography/Typography';
import { FlexCenter } from '@/components/Helpers';
import { Link } from '@/components/Link';
import { useTranslations } from 'next-intl';
import { device, HideAbove, HideBelow } from '@/components/Media';
import Logo from '@/components/Logo/Logo';

export const AuthPageContainer = styled(FlexCol)`
    align-self: start;
    justify-content: space-between;
    min-height: 484px;

    @media screen and ${device.laptopS} {
        align-self: center;
    }
`;

export const PageTitle = styled(Typography).attrs({
    variant: 'uiType',
    size: '3xl',
    modifier: 'S'
})`
    text-align: center;
    display: block;
    color: ${props => props.theme.colors.palette.grey_800};

    @media screen and ${device.laptopS} {
        font-size: ${({ theme }) => theme.typography.uiType['xl'].S.size};
        line-height: ${({ theme }) => theme.typography.uiType['xl'].S.lineHeight};
        letter-spacing: ${({ theme }) => theme.typography.uiType['xl'].S.letterSpacing};
    }
`;

export const FormContainer = styled(FlexCol)`
    margin-top: 54px;
    margin-left: auto;
    margin-right: auto;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.md};

    @media ${device.laptopS} {
        margin-top: ${({ theme }) => theme.spacing['3xs']};
        gap: ${({ theme }) => theme.spacing['2xs']};
    }
`;

export const MainContent = styled(FlexCol)`
    flex-grow: 1;

    @media ${device.laptopS} {
        gap: ${({ theme }) => theme.spacing['2xs']};
        justify-content: center;
    }
`;

export const CenteredFlex3XS = makeCentered(Flex3XS, {
    x: true
});

export const CenteredFlex4XS = styled(makeCentered(Flex4XS, {
    x: true,
    y: true
}))`
    
`;

const HelpWrapper = styled(CenteredFlex4XS)<{ withDivider: boolean }>`
    ${({ withDivider }) => withDivider && css`
        @media screen and ${device.laptopS} {
            border-top: 1px solid ${({ theme }) => theme.colors.palette.grey_450};
        }
    `};
`;

export const GreyTypography = styled(Typography)`
    color: ${({ theme }) => theme.colors.palette.grey_700};
    white-space: pre;

    @media screen and ${device.laptopS} {
        white-space: unset;
        font-size: ${({ theme }) => theme.typography.uiType['sm'].S.size};
        line-height: 21px;
        letter-spacing: ${({ theme }) => theme.typography.uiType['sm'].S.letterSpacing};
    }
`;

export const FlexFitCenter = styled(FlexCenter)`
    height: 100%;
    flex-grow: 1;

    @media screen and ${device.laptopS} {
        flex-grow: 0;
    }
`;

const GreyTypographyWithCircle = styled(GreyTypography)`
    position: relative;

    &::after {
        content: '';
        background-color: #fff;
        border-radius: 2046px;
        height: 2046px;
        width: 2046px;
        position: absolute;
        left: -961px;
        bottom: -10px;
        z-index: -999;
    }
`;

const MoonOneLogoWrapper = styled(Flex)`
    justify-content: center;
`;

export const NeedHelpBlock = ({ withDivider = true }) => {
    const t = useTranslations();

    return (
        <CenteredFlex4XS>
            <HelpWrapper withDivider={withDivider}>
                <HideBelow md>
                    <GreyTypographyWithCircle variant='uiType' size='sm' modifier='R'>{t('auth.needHelp')}</GreyTypographyWithCircle>
                </HideBelow>
                <HideAbove md>
                    <GreyTypography variant='uiType' size='sm' modifier='R'>{t('auth.needHelp')}</GreyTypography>
                </HideAbove>
                &nbsp;
                <Link href='https://t.me/moondmc_bot' target='_blank' variant='uiType' size='sm' modifier='S'>{t('auth.contactUs')}</Link>
            </HelpWrapper>
        </CenteredFlex4XS>
    );
};

export const MoonOneLogo = () => {
    return (
        <MoonOneLogoWrapper>
            <Logo width='102px' height='36px' />
        </MoonOneLogoWrapper>
    );
};
